import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';
import ReactPlayer from 'react-player/youtube';

// Import components
import { FaDrum, FaMusic, FaRegCalendarAlt } from 'react-icons/fa';

// Import images - use numbering from assets
import heroImage1 from '../assets/images/1.png';
import heroImage2 from '../assets/images/2.jpg';
import heroImage3 from '../assets/images/3.png';

// Import service images
import drumInstituteImage from '../assets/images/Drum Institute/1.jpg';
import weddingServiceImage from '../assets/images/Wedding Services/1.jpg';
import corporateEventImage from '../assets/images/Coporate Events/4.jpg';

// Import video thumbnail
import videoThumbnail from '../assets/images/Drum Institute/9.jpg'; // Create this file by taking a screenshot of the video

const HomePage: React.FC = () => {
  const [videoPlaying, setVideoPlaying] = useState(false);
  
  // Hero slider images - using actual images from assets
  const heroImages = [
    heroImage1,
    heroImage2,
    heroImage3,
  ];
  
  // Featured services
  const services = [
    {
      id: 1,
      title: "Drum Institute",
      description: "Learn traditional Sri Lankan drums and dance from expert instructors",
      icon: <FaDrum className="h-8 w-8" />,
      image: drumInstituteImage,
      link: "/institute"
    },
    {
      id: 2,
      title: "Wedding Services",
      description: "Authentic Poruwa ceremonies and wedding entertainment",
      icon: <FaRegCalendarAlt className="h-8 w-8" />,
      image: weddingServiceImage,
      link: "/wedding-services"
    },
    {
      id: 3,
      title: "Corporate Events",
      description: "Cultural performances for corporate events and functions",
      icon: <FaMusic className="h-8 w-8" />,
      image: corporateEventImage,
      link: "/corporate-events"
    }
  ];

  // Testimonials
  const testimonials = [
    {
      id: 1,
      quote: "The best place to train your child with the traditional drums. Rohan Shaminda sir is a very loving and knowledgeable teacher giving all his efforts to pass on the tradition to the future.",
      author: "Nadeesha Lokuge",
      role: "Parent"
    },
    {
      id: 2,
      quote: "This was an excellent class. I highly recommend Rivisara Drum Institute to anyone looking to develop their drumming skills.",
      author: "Thashika Nimeshi",
      role: "Student"
    },
    {
      id: 3,
      quote: "Best place in Sri Lanka to learn all types of traditional and modern drumming",
      author: "Chethal Mataraarachchi",
      role: "Parent"
    }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <>
      {/* Hero Section with Dynamic Slider */}
      <section className="relative h-screen">
        <Swiper
          modules={[Autoplay, EffectFade, Pagination]}
          effect="fade"
          pagination={{ clickable: true }}
          autoplay={{ delay: 5000 }}
          loop={true}
          className="h-full w-full"
        >
          {heroImages.map((image, index) => (
            <SwiperSlide key={index}>
              <div 
                className="h-full w-full bg-cover bg-center bg-fixed"
                style={{ backgroundImage: `url(${image})` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-black to-transparent opacity-70"></div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        
        <div className="absolute inset-0 flex items-center z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="max-w-xl"
            >
              <h1 className="text-5xl font-bold font-display tracking-tight text-white sm:text-6xl lg:text-7xl drop-shadow-lg">
                Experience <span className="text-orange-400">Sri Lankan</span> Rhythms
              </h1>
              <p className="mt-6 text-xl text-gray-100 max-w-3xl drop-shadow-lg">
                Authentic cultural performances, education, and entertainment services 
                that bring the vibrant traditions of Sri Lanka to life.
              </p>
              
              <div className="mt-10 flex flex-wrap gap-4">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link
                    to="/institute"
                    className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-full text-white bg-orange-600 hover:bg-orange-700 transition-all shadow-lg"
                  >
                    Our Institute
                  </Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link
                    to="/wedding-services"
                    className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-full text-orange-700 bg-white hover:bg-gray-100 transition-all shadow-lg"
                  >
                    Wedding Services
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
        
        {/* Decorative elements - Updated for better alignment */}
        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden z-10">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 1200 120" 
            preserveAspectRatio="none" 
            className="w-full h-16 md:h-20"
          >
            <path 
              fill="#ffffff" 
              fillOpacity="1" 
              d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" 
              opacity=".25" 
              className="shape-fill"
              transform="rotate(180 600 60)"
            />
            <path 
              fill="#ffffff" 
              fillOpacity="1" 
              d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" 
              opacity=".5" 
              className="shape-fill"
              transform="rotate(180 600 60)"
            />
            <path 
              fill="#ffffff" 
              fillOpacity="1" 
              d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" 
              className="shape-fill"
              transform="rotate(180 600 60)"
            />
          </svg>
        </div>
        
        {/* Animated drums icon */}
        <div className="absolute bottom-24 right-10 hidden lg:block z-10">
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <FaDrum className="h-12 w-12 text-orange-400" />
          </motion.div>
        </div>
      </section>

      {/* Featured Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true, amount: 0.2 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold font-display text-gray-900">
              Our <span className="text-orange-600">Services</span>
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our comprehensive range of cultural and entertainment services
            </p>
          </motion.div>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-10"
          >
            {services.map((service) => (
              <motion.div 
                key={service.id} 
                variants={itemVariants}
                className="bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all hover:-translate-y-2 hover:shadow-2xl"
              >
                <div className="relative h-64">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="h-full w-full"
                  />
                  <div className="absolute top-4 right-4 bg-orange-600 rounded-full p-3 shadow-lg">
                    <div className="text-white">
                      {service.icon}
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900">{service.title}</h3>
                  <p className="mt-2 text-gray-600">{service.description}</p>
                  <div className="mt-6">
                    <Link 
                      to={service.link}
                      className="inline-flex items-center text-orange-600 font-medium hover:text-orange-800"
                    >
                      Learn more
                      <svg className="ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Video Showcase */}
      <section className="py-20 bg-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-900 to-orange-800 opacity-50"></div>
        <div className="absolute inset-0">
          <div className="bg-pattern w-full h-full bg-repeat opacity-10"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold font-display">
              Experience Our <span className="text-orange-400">Performances</span>
            </h2>
            <p className="mt-4 text-xl text-gray-300 max-w-3xl mx-auto">
              Watch our performers bring the traditional rhythms and dances of Sri Lanka to life
            </p>
          </motion.div>
          
          <div className="aspect-w-16 aspect-h-9 relative">
            <div className="w-full h-0 pb-[56.25%] relative rounded-2xl overflow-hidden shadow-2xl">
              {!videoPlaying ? (
                <div className="absolute inset-0 bg-black flex items-center justify-center">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="rounded-full bg-orange-600 p-5 shadow-xl z-20"
                    onClick={() => setVideoPlaying(true)}
                  >
                    <svg className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </motion.button>
                  {/* Fix thumbnail display */}
                  <img 
                    src={videoThumbnail} 
                    alt="Video thumbnail" 
                    className="absolute inset-0 w-full h-full object-cover object-center"
                    style={{ opacity: 0.6 }}
                  />
                </div>
              ) : (
                <div className="absolute inset-0">
                  <ReactPlayer
                    url="https://youtu.be/AbzeH6o77lc"
                    width="100%"
                    height="100%"
                    playing
                    controls
                    config={{
                      playerVars: { 
                        showinfo: 1,
                        controls: 1,
                        autoplay: 1
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="inline-block mb-4 p-3 bg-orange-600/30 rounded-full">
                <FaDrum className="h-8 w-8 text-orange-400" />
              </div>
              <h3 className="text-xl font-bold">Traditional Drumming</h3>
              <p className="mt-2 text-gray-300">Authentic Sri Lankan drumming performances showcasing rhythmic patterns</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="inline-block mb-4 p-3 bg-orange-600/30 rounded-full">
                <FaMusic className="h-8 w-8 text-orange-400" />
              </div>
              <h3 className="text-xl font-bold">Folk Dances</h3>
              <p className="mt-2 text-gray-300">Energetic cultural dances showcasing Sri Lanka's rich heritage</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="inline-block mb-4 p-3 bg-orange-600/30 rounded-full">
                <FaRegCalendarAlt className="h-8 w-8 text-orange-400" />
              </div>
              <h3 className="text-xl font-bold">Special Events</h3>
              <p className="mt-2 text-gray-300">Customized performances for weddings, corporate events, and celebrations</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Testimonials */}
      <section className="py-20 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold font-display text-gray-900">
              What Our <span className="text-orange-600">Clients Say</span>
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <motion.div 
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: testimonial.id * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg p-8 relative"
              >
                <div className="absolute -top-4 -left-4 text-6xl text-orange-400 opacity-50 pointer-events-none">
                  "
                </div>
                <p className="text-gray-600 italic relative z-10">"{testimonial.quote}"</p>
                <div className="mt-6">
                  <p className="font-bold text-gray-900">{testimonial.author}</p>
                  <p className="text-sm text-orange-600">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-16 text-center">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link 
                to="/contact#booking"
                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-full text-white bg-orange-600 hover:bg-orange-700 transition-all shadow-lg"
              >
                Book Your Experience
                <svg className="ml-2 -mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Call to Action */}
      <section className="py-16 bg-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold font-display">Ready to experience authentic Sri Lankan culture?</h2>
            <p className="mt-2 text-orange-100">Book our services for your next event or enroll in our institute.</p>
          </div>
          <div className="mt-6 md:mt-0">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link 
                to="/contact"
                className="inline-flex items-center px-8 py-3 border border-white text-base font-medium rounded-full text-orange-600 bg-white hover:bg-orange-50 transition-all shadow-lg"
              >
                Contact Us
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
